import AddIcon from '@mui/icons-material/Add';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { Box, ClickAwayListener, Popper } from '@mui/material';
import { useAtomValue } from 'jotai';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateTextAtomAnnotations } from '@/api/mutations/useTextAtomMutations';
import { activeLineIdAtom, draggedLineIdAtom } from '@/atoms/lines';
import { filmPlanVisibilityAtom } from '@/atoms/visibility';
import { LineSelectionPopover } from '@/components/LineSelectionPopover/LineSelectionPopover';
import { useGetLineColor, useRequiredAppSelector } from '@/hooks';
import { useReadOnlyGate } from '@/hooks/useReadOnlyGate';
import type { ILine, TextAtomContentJson } from '@/types';
import { makeLineGradient } from '@/utils/lineColorUtils';
import { nonNullable } from '@/utils/nonNullable';
import {
    applyLineSelectionToParagraph,
    type ContentJsonData,
    getAllLineIdsForParagraph,
    type ParagraphLineEntry,
    parseTextIntoParagraphs,
    splitTextByRanges,
    toggleLineInData,
} from '@/utils/paragraphLines';
import { LineTagPill } from './LineTagPill';

interface AnnotatedTextProps {
    content: string;
    annotationKey: string;
    lines: ILine[];
    textAtomId?: number;
    contentJson?: TextAtomContentJson;
    showLineTags?: boolean;
    showLineFocus?: boolean;
}

const parseContentJson = (raw?: TextAtomContentJson): ContentJsonData => {
    if (!raw) return {};
    if (Array.isArray(raw)) return (raw[0] ?? {}) as ContentJsonData;
    if (typeof raw === 'object') return raw as ContentJsonData;
    try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed[0] ?? {};
        return parsed;
    } catch {
        return {};
    }
};

const makeGradient = (colors: string[]) => makeLineGradient(colors, 0.1);
const makeSegmentGradient = (colors: string[]) => makeLineGradient(colors, 0.14);

/** Tag click target: which paragraph + which line the action strip operates on. */
interface TagMenuState {
    anchorEl: HTMLElement;
    paragraphIndex: number;
    lineId: number;
}

interface AddPopoverState {
    anchorEl: HTMLElement;
    paragraphIndex: number;
}

export const AnnotatedText = memo(({ content, lines, textAtomId, contentJson, showLineTags, showLineFocus }: AnnotatedTextProps) => {
    const { t } = useTranslation();
    const activeLineId = useAtomValue(activeLineIdAtom);
    const draggedLineId = useAtomValue(draggedLineIdAtom);
    const visibility = useAtomValue(filmPlanVisibilityAtom);
    const { isReadOnly } = useReadOnlyGate();
    const resolvedShowLineTags = showLineTags ?? visibility.lineTags ?? true;
    const resolvedShowLineFocus = showLineFocus ?? visibility.lineFocus ?? true;
    const getLineColor = useGetLineColor();
    const projectId = useRequiredAppSelector('projectId');
    const containerRef = useRef<HTMLDivElement>(null);
    const linesById = useMemo(() => new Map(lines.map(l => [l.id, l])), [lines]);

    const serverData = useMemo(() => parseContentJson(contentJson), [contentJson]);

    const [localData, setLocalData] = useState<ContentJsonData>(serverData);
    const [tagMenu, setTagMenu] = useState<TagMenuState | null>(null);
    const [addPopover, setAddPopover] = useState<AddPopoverState | null>(null);
    const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

    useEffect(() => {
        setLocalData(serverData);
    }, [serverData]);

    useEffect(() => {
        if (draggedLineId === null) setDragOverIndex(null);
    }, [draggedLineId]);

    const paragraphs = useMemo(() => parseTextIntoParagraphs(content), [content]);
    const hasAnnotations = (localData.paragraph_lines?.length ?? 0) > 0;

    const activeLine = useMemo(() => (activeLineId ? lines.find(l => l.id === activeLineId) : null), [activeLineId, lines]);
    const activeColor = activeLine ? getLineColor(activeLine.color) : null;
    const isPaintingMode = !!activeLine && !!textAtomId;
    const canManageLines = !isReadOnly && !!textAtomId;

    const draggedLine = draggedLineId !== null ? (linesById.get(draggedLineId) ?? null) : null;

    const annotationsMutation = useUpdateTextAtomAnnotations();
    const saveAnnotations = useCallback(
        (newData: ContentJsonData) => {
            if (!textAtomId) return;
            annotationsMutation.mutate({ project_id: projectId, id: textAtomId, content_json: newData });
        },
        [annotationsMutation.mutate, projectId, textAtomId],
    );

    // Single write path for every paragraph-lines change. Handlers compute the
    // next entries from the current `localData` (they all run on discrete user
    // events) and commit here — never inside a setState updater, which must
    // stay pure (StrictMode double-invokes updaters and would double-fire the
    // save).
    const commitParagraphLines = useCallback(
        (updatedParagraphLines: ParagraphLineEntry[]) => {
            const next = { ...localData, paragraph_lines: updatedParagraphLines };
            setLocalData(next);
            saveAnnotations(next);
        },
        [localData, saveAnnotations],
    );

    const handleParagraphClick = useCallback(
        (paragraphIndex: number, e: React.MouseEvent) => {
            if (!activeLineId || !textAtomId) return;
            e.stopPropagation();
            commitParagraphLines(toggleLineInData(localData, paragraphIndex, activeLineId));
        },
        [activeLineId, textAtomId, localData, commitParagraphLines],
    );

    const handleTagClick = useCallback((paragraphIndex: number, lineId: number, e: React.SyntheticEvent<HTMLElement>) => {
        e.stopPropagation();
        // Capture before setState: React nulls `currentTarget` once the event
        // finishes dispatching, and the updater callback runs after that.
        const anchorEl = e.currentTarget;
        setAddPopover(null);
        setTagMenu(prev =>
            prev && prev.paragraphIndex === paragraphIndex && prev.lineId === lineId ? null : { anchorEl, paragraphIndex, lineId },
        );
    }, []);

    const handleRemoveLine = useCallback(() => {
        if (!tagMenu) return;
        commitParagraphLines(toggleLineInData(localData, tagMenu.paragraphIndex, tagMenu.lineId));
        setTagMenu(null);
    }, [tagMenu, localData, commitParagraphLines]);

    const handleOpenAddPopover = useCallback(() => {
        if (!tagMenu) return;
        setAddPopover({ anchorEl: tagMenu.anchorEl, paragraphIndex: tagMenu.paragraphIndex });
        setTagMenu(null);
    }, [tagMenu]);

    const handleApplyParagraphLines = useCallback(
        (lineIds: number[]) => {
            if (addPopover) {
                const updated = applyLineSelectionToParagraph(localData, addPopover.paragraphIndex, lineIds);
                // Apply with an unchanged selection must not fire a save.
                if (JSON.stringify(updated) !== JSON.stringify(localData.paragraph_lines ?? [])) {
                    commitParagraphLines(updated);
                }
            }
            setAddPopover(null);
        },
        [addPopover, localData, commitParagraphLines],
    );

    const handleDrop = useCallback(
        (paragraphIndex: number, e: React.DragEvent) => {
            if (draggedLineId === null || !canManageLines) return;
            e.preventDefault();
            e.stopPropagation();
            setDragOverIndex(null);
            const entry = localData.paragraph_lines?.find(p => p.index === paragraphIndex);
            const alreadyAttached = entry ? getAllLineIdsForParagraph(entry).includes(draggedLineId) : false;
            if (alreadyAttached) return;
            commitParagraphLines(toggleLineInData(localData, paragraphIndex, draggedLineId));
        },
        [draggedLineId, canManageLines, localData, commitParagraphLines],
    );

    if (!hasAnnotations && !isPaintingMode && !textAtomId) return <>{content}</>;

    const addPopoverEntry = addPopover ? localData.paragraph_lines?.find(p => p.index === addPopover.paragraphIndex) : undefined;

    return (
        <div ref={containerRef}>
            {paragraphs.map((text, idx) => {
                const entry = localData.paragraph_lines?.find(p => p.index === idx);
                const wholeLineIds = entry?.line_ids ?? [];
                const wholeAssociatedLines = wholeLineIds.map(id => linesById.get(id)).filter(nonNullable);
                const wholeLineColors = wholeAssociatedLines.map(l => getLineColor(l.color));
                const hasWholeLines = wholeAssociatedLines.length > 0;

                const ranges = entry?.ranges ?? [];
                const hasRanges = ranges.length > 0;

                const allLineIds = entry ? getAllLineIdsForParagraph(entry) : wholeLineIds;
                const allAssociatedLines = allLineIds.map(id => linesById.get(id)).filter(nonNullable);

                const hasInRanges = isPaintingMode && activeLine && (entry?.ranges?.some(r => r.line_ids.includes(activeLine.id)) ?? false);
                const willRemove = isPaintingMode && activeLine && (wholeLineIds.includes(activeLine.id) || hasInRanges);
                const previewColors =
                    isPaintingMode && activeColor
                        ? willRemove
                            ? wholeLineColors.filter((_, i) => wholeAssociatedLines[i]?.id !== activeLine?.id)
                            : [...wholeLineColors, activeColor]
                        : null;

                const segments = hasRanges && resolvedShowLineFocus ? splitTextByRanges(text, ranges) : null;

                // Drop preview while a toolbar chip hovers this paragraph.
                const isDropTarget = canManageLines && draggedLine !== null;
                const dragPreview =
                    isDropTarget && dragOverIndex === idx
                        ? makeSegmentGradient([...wholeLineColors, getLineColor(draggedLine.color)])
                        : null;

                return (
                    <Box
                        key={idx}
                        component="span"
                        data-paragraph-index={idx}
                        onClick={isPaintingMode ? e => handleParagraphClick(idx, e) : undefined}
                        onDragOver={
                            isDropTarget
                                ? e => {
                                      e.preventDefault();
                                      e.dataTransfer.dropEffect = 'copy';
                                      setDragOverIndex(idx);
                                  }
                                : undefined
                        }
                        onDragLeave={isDropTarget ? () => setDragOverIndex(prev => (prev === idx ? null : prev)) : undefined}
                        onDrop={isDropTarget ? e => handleDrop(idx, e) : undefined}
                        sx={{
                            display: 'block',
                            lineHeight: 1.4,
                            wordBreak: 'break-word',
                            background: dragPreview ?? (resolvedShowLineFocus ? makeGradient(wholeLineColors) : 'none'),
                            ...((hasWholeLines || dragPreview) &&
                                (resolvedShowLineFocus || dragPreview) && {
                                    borderRadius: '2px',
                                    padding: '1px 2px',
                                }),
                            ...(isPaintingMode && {
                                cursor: 'pointer',
                                borderRadius: '2px',
                                transition: 'background 0.15s ease',
                                '&:hover': {
                                    background: resolvedShowLineFocus ? makeGradient(previewColors ?? []) : 'none',
                                },
                            }),
                        }}
                    >
                        {resolvedShowLineTags &&
                            allAssociatedLines.map(line => (
                                <LineTagPill
                                    key={line.id}
                                    line={line}
                                    color={getLineColor(line.color)}
                                    onClick={canManageLines && !isPaintingMode ? e => handleTagClick(idx, line.id, e) : undefined}
                                />
                            ))}
                        {segments
                            ? segments.map((seg, i) => {
                                  if (seg.line_ids.length === 0) return <span key={i}>{seg.text}</span>;
                                  const segLines = seg.line_ids.map(id => linesById.get(id)).filter(nonNullable);
                                  const segColors = segLines.map(l => getLineColor(l.color));
                                  return (
                                      <span
                                          key={i}
                                          style={{
                                              background: makeSegmentGradient(segColors),
                                              borderRadius: '2px',
                                              padding: '1px 0',
                                          }}
                                      >
                                          {seg.text}
                                      </span>
                                  );
                              })
                            : text}
                    </Box>
                );
            })}
            {tagMenu && (
                <Popper
                    open
                    anchorEl={tagMenu.anchorEl}
                    placement="bottom-start"
                    modifiers={[{ name: 'offset', options: { offset: [0, 4] } }]}
                    className="z-[1300]"
                >
                    <ClickAwayListener
                        onClickAway={ev => {
                            // Pill clicks manage the menu themselves (toggle / re-anchor) —
                            // letting the click-away fire too would immediately close it.
                            const target = ev.target instanceof Element ? ev.target : null;
                            if (target?.closest('[data-line-marker]')) return;
                            setTagMenu(null);
                        }}
                    >
                        {/* The Popper portal still bubbles React events through this
                            component tree — without this stop, strip clicks reach the
                            EditableSynopsis Typography and flip it into edit mode. */}
                        <div
                            role="presentation"
                            className="flex gap-1 rounded-lg bg-c1 p-1 shadow-md dark:bg-c37"
                            data-line-popup
                            onClick={e => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                className="flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-xs text-text-primary hover:bg-c5 dark:text-c1 dark:hover:bg-c16"
                                onClick={handleRemoveLine}
                            >
                                <LinkOffIcon style={{ fontSize: '13px' }} />
                                {t('lines.remove-line')}
                            </button>
                            <button
                                type="button"
                                className="flex cursor-pointer items-center gap-1 rounded px-2 py-1 text-xs text-text-primary hover:bg-c5 dark:text-c1 dark:hover:bg-c16"
                                onClick={handleOpenAddPopover}
                            >
                                <AddIcon style={{ fontSize: '13px' }} />
                                {t('lines.add-line')}
                            </button>
                            <button
                                type="button"
                                className="cursor-not-allowed rounded px-2 py-1 text-xs text-c4"
                                disabled
                                title={t('lines.in-development')}
                            >
                                {t('lines.add-comment')}
                            </button>
                        </div>
                    </ClickAwayListener>
                </Popper>
            )}
            {addPopover && (
                <LineSelectionPopover
                    lines={lines}
                    selectedLineIds={addPopoverEntry ? getAllLineIdsForParagraph(addPopoverEntry) : []}
                    anchorEl={addPopover.anchorEl}
                    onApply={handleApplyParagraphLines}
                    onClose={() => setAddPopover(null)}
                />
            )}
        </div>
    );
});

AnnotatedText.displayName = 'AnnotatedText';
